import DateTime from '@newspaces/zero-base/DateTime';
import Model from '@newspaces/zero-base/Model';
import '../i18n.js';
import i18n from '@newspaces/zero-base/i18n';

const getDayOfYear366 = (month, date) => {
  if (typeof month !== "number" || month < 1 || month > 12) {
    return 0;
  }
  if (typeof date !== "number" || date < 1 || date > 31) {
    return 0;
  }
  const totalDaysInMonth = new DateTime.Date({ year: 2020, month: month - 1, date: 1 }).totalDaysInMonth;
  if (totalDaysInMonth < date) {
    return 0;
  }
  return new DateTime.Date({ year: 2020, month: month - 1, date }).dayOfYear366;
};
class AccessTimeSchedule extends Model {
  static formatWeekdaysString = (weekdays) => {
    const wdays = [];
    for (let weekdayIndex = 0; weekdayIndex < weekdays.length; weekdayIndex += 1) {
      if (weekdays[weekdayIndex]) {
        const startDayIndex = weekdayIndex;
        let endDayIndex = startDayIndex;
        while (endDayIndex + 1 < weekdays.length && weekdays[endDayIndex + 1]) {
          endDayIndex += 1;
        }
        const startDay = i18n().weekdaysMin[startDayIndex === 6 ? 0 : startDayIndex + 1];
        const endDay = i18n().weekdaysMin[endDayIndex === 6 ? 0 : endDayIndex + 1];
        if (startDayIndex !== endDayIndex) {
          wdays.push(`${startDay}-${endDay}`);
          weekdayIndex = endDayIndex;
        } else {
          wdays.push(startDay);
        }
      }
    }
    return wdays.join(", ");
  };
  static Fields = {
    monthStart: {
      type: Number,
      min: 1,
      max: 12,
      required: true,
      default: 1
    },
    monthEnd: {
      type: Number,
      min: 1,
      max: 12,
      required: true,
      default: 12
    },
    dateStart: {
      type: Number,
      min: 1,
      max: 31,
      required: true,
      default: 1
    },
    dateEnd: {
      type: Number,
      min: 1,
      max: 31,
      required: true,
      default: 31
    },
    timeStart: {
      type: DateTime.Time,
      required: true,
      validate: ({ timeStart }) => {
        if (timeStart) {
          const time = new DateTime.Time(timeStart, { forceUTC: true });
          if (time.hours > 23 || time.minutes > 59) {
            return "invalidTime";
          }
        }
        return null;
      }
    },
    timeEnd: {
      type: DateTime.Time,
      required: true
    },
    dayOfYearStart: {
      type: Number,
      get: true
    },
    dayOfYearEnd: {
      type: Number,
      get: true
    },
    weekdays: {
      type: [Boolean],
      required: true,
      validate: ({ weekdays }) => {
        if (weekdays.length !== 7) {
          return "notSevenWeekdays";
        }
        const hasOneWeekday = weekdays.filter((d) => !!d).length > 0;
        if (!hasOneWeekday) {
          return "noWeekday";
        }
        return null;
      }
    }
  };
  constructor(data) {
    super(AccessTimeSchedule.Fields, data);
  }
  get dayOfYearStart() {
    return getDayOfYear366(this.monthStart, this.dateStart);
  }
  get dayOfYearEnd() {
    return getDayOfYear366(this.monthEnd, this.dateEnd);
  }
  validate(parentPath = "", returnErrors = false) {
    let errors = super.validate(parentPath, returnErrors);
    if (errors?.length) {
      return errors;
    }
    const getPath = (path) => {
      if (parentPath) {
        return `${parentPath}.${path}`;
      }
      return path;
    };
    errors = [];
    if (this.monthStart > this.monthEnd) {
      errors.push({ path: getPath("monthStart"), type: "startAfterEnd" });
    } else if (this.monthStart === this.monthEnd && this.dateStart > this.dateEnd) {
      errors.push({ path: getPath("dateStart"), type: "startAfterEnd" });
    }
    if (this.timeStart.isAfter(this.timeEnd)) {
      errors.push({ path: getPath("timeStart"), type: "startAfterEnd" });
    }
    if (this.timeEnd) {
      const isMidnightAllowed = parentPath.includes("connectSchedules");
      const time = new DateTime.Time(this.timeEnd, { forceUTC: true });
      if (!isMidnightAllowed && time.hours === 0 && time.minutes === 0 || time.hours > 24 || time.minutes > 59 || time.hours === 24 && time.minutes > 0) {
        errors.push({ path: getPath("timeEnd"), type: "invalidTime" });
      }
    }
    if (errors.length) {
      if (returnErrors) {
        return errors;
      }
      throw new Model.ValidationError(errors);
    }
    return null;
  }
  get description() {
    if (!this.weekdays?.length || !this.dayOfYearStart || !this.dayOfYearEnd || !this.timeStart || !this.timeEnd) {
      return "";
    }
    const weekdaysStr = AccessTimeSchedule.formatWeekdaysString(this.weekdays);
    const tfrom = i18n().formatDate(this.timeStart);
    const tto = i18n().formatDate(this.timeEnd);
    let daysLimit = "";
    if (this.dayOfYearStart > 1 || this.dayOfYearEnd < 366) {
      daysLimit = ` | ${i18n().monthsShort[this.monthStart - 1]} ${this.dateStart}-${i18n().monthsShort[this.monthEnd - 1]} ${this.dateEnd}`;
    }
    return `\u{1F552} ${tfrom}-${tto} (${weekdaysStr}${daysLimit})`;
  }
}

export { AccessTimeSchedule as default };
//# sourceMappingURL=AccessTimeSchedule.js.map
